<template>
    <p-feature-flag
          name="Quick Menu"
          redirect
      >
        <b-overlay
            :show="isBusy"
            rounded
            opacity="0.5"
            spinner-small
            spinner-variant="primary"
            class="d-inline-block container-fluid content-wrapper"
            >
            <p-form @submit="onSubmit">
                <div v-if="quickMenu">
                    <b-row class="content-heading">
                        <b-col cols="2">
                            <div>{{ isNew ? 'Add' : 'Edit' }} Quick Menu Profile</div>
                        </b-col>
                        <b-col cols="auto" class="ms-auto">
                            <p-button 
                                type="submit" 
                                variant="primary" 
                                :is-busy="isBusy"
                                >Save</p-button
                            >
                        </b-col>
                    </b-row>
                    <p-card>
                        <b-row cols="6">
                            <b-col cols="3">
                                <p-input
                                    label="Name"
                                    v-model="quickMenu.name"
                                    rules="required|max:50"
                                />
                            </b-col>
                            <b-col style="max-width: 205px;" class="mt-5" >
                                <p-checkbox 
                                    v-model="quickMenu.menuType"
                                    label="Persist Quick Menu"
                                    info-message="Selecting this option will persist your quickmenu navigation after selecting an item" />
                            </b-col>
                            <b-col class="mt-5">
                                <p-checkbox 
                                    v-model="quickMenu.default"
                                    label="Default" />
                            </b-col>                              
                        </b-row>
                        <b-row style="padding-top: 10px;">
                            <b-col class="content-wrapper">
                                <h4>Product Search</h4>
                                <p-card class="product-search-container">
                                    <ProductDraggableSearch
                                        place-holder="Search by Sku, Description"
                                        end-point="/quickmenus/searchproducts"
                                        style="max-height: 650px"
                                        group="Search"
                                        >
                                            <template v-slot:headerRow>
                                                <b-row class="mt-3">
                                                    <b-col style="width: 425px;">
                                                        <h4>Description</h4>
                                                    </b-col>
                                                    <b-col cols="auto" class="ms-auto" style="width: 70px;">
                                                        <h4>Sku</h4>
                                                    </b-col>
                                                    <b-col cols="auto" class="ms-auto" style="text-align: right; margin-right: 45px; width: 100px">
                                                        <h4>Price</h4>
                                                    </b-col>
                                                </b-row>
                                            </template>
                                            <template v-slot:itemText="{ value, item }">
                                                <b-row :item="item" class="ps-2"> <!-- this :item property is used for passing the item to the draggable component-->
                                                    <b-col class="text-truncate" style="width: 425px;">{{ item.containerName }}</b-col>
                                                    <b-col cols="auto" class="ms-auto" style="width: 95px;" >{{ item.sku }}</b-col> 
                                                    <b-col cols="auto" class="ms-auto" style="width: 100px;">{{ currencyFormatter(item.price) }}</b-col>
                                                </b-row>
                                            </template>
                                    </ProductDraggableSearch>
                                </p-card>
                            </b-col>
                            <b-col class="content-wrapper" >
                                <b-row>
                                    <b-col style="margin-bottom: -10px">
                                        <b-row>
                                            <b-col style="align-self: flex-end;min-width:80px;white-space: nowrap" cols="9" sm="6" xl="3">
                                                <h4>Containers</h4>
                                            </b-col>
                                            <b-col>
                                                <p-button class="pb-1 pe-3" variant="outline-primary" @click="addPage">Add Page</p-button>
                                                <p-button class="pb-1 pe-3" variant="outline-primary" @click="addContainer">Add Container</p-button>
                                            </b-col>
                                        </b-row>
                                        <p-card style="overflow: auto; height: 380px;">
                                            <p-draggable-tree
                                                name="pTree1"
                                                style="margin-left: -35px"
                                                v-model="quickMenu.specialItemProfileGroups"
                                                node-text-field="containerName"
                                                node-id-field="specialItemGroupItemId"
                                                container-text-field="containerName"
                                                container-id-field="specialItemProfileGroupId"
                                                child-array-name="specialItemGroupItems"
                                                :show-lock-condition="showLock"
                                                :pull="['Search','Repeatables']"
                                                group="Containers"
                                                @item-selected="e => itemSelected(e, false)"
                                                />
                                        </p-card>
                                    </b-col>
                                </b-row>
                                <b-row class="pb-2">
                                    <b-col>
                                        <p-checkbox v-for="type in specialContainerTypes"
                                            :key="type.key"
                                            inline
                                            :checked="isSpecialContainerSelected(type)"
                                            @change="toggleSpecialContainer(type)"
                                            :label="type.value"
                                            class="mx-2"
                                            />
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col>
                                        <b-row>
                                            <b-col style="align-self: flex-end;">
                                                <h4>Repeatable Containers</h4>
                                            </b-col>
                                            <b-col>
                                                <p-button 
                                                    class="pb-1 pe-3" 
                                                    variant="outline-primary"
                                                    @click="addRepeatableContainer">Add Repeatable Container</p-button>
                                            </b-col>
                                        </b-row>                                        
                                        <p-card>
                                            <div style="overflow-x: hidden; overflow-y: auto; height: 160px; width: 100%; min-width: 0;">
                                                <p-draggable-tree
                                                    name="pTree2"
                                                    style="margin-left: -35px; max-width: 100%; min-width: 0;"
                                                    v-model="quickMenu.repeatablesContainer.specialItemGroupItems"
                                                    node-text-field="containerName"
                                                    node-id-field="specialItemGroupItemId"
                                                    container-text-field="containerName"
                                                    container-id-field="specialItemProfileGroupId"  
                                                    child-array-name="specialItemGroupItems"
                                                    group="Repeatables"
                                                    :pull-option="(to) => to.options.group.name === 'Containers' ? 'clone' : false"
                                                    :put-option="['Search']"
                                                    :clone="deepClone"
                                                    enable-only-drag-containers
                                                    @item-selected="e => itemSelected(e,true)"
                                                    />
                                            </div>
                                        </p-card>
                                    </b-col>
                                </b-row>
                            </b-col>
                            <b-col class="content-wrapper">
                                <h4>Selected Item</h4>
                                <p-card style="max-height: 93%; height: 93%">
                                    <div v-if="selectedItem">
                                        <div v-if="!selectedItem.isContainer">
                                            <p-input label="Button Label" v-model="selectedItem.containerName" />
                                            <b-row class="mt-3">
                                                <b-col>
                                                    <p-color-picker style="margin-left: 15px; margin-bottom: 10px" label="Button Color" v-model="selectedItem.buttonColor" />
                                                </b-col>
                                                <b-col cols="auto">
                                                    <span>Button Image
                                                        <span v-b-tooltip.hover title="Web Kiosk Only">
                                                            <b-icon-info-circle></b-icon-info-circle>
                                                        </span>
                                                    </span>
                                                    <p-image ref="quickMenuImage" :key="selectedItem.specialItemGroupItemId" v-model="selectedItem.buttonImage" small />
                                                </b-col>
                                            </b-row>
                                            <p-input label="Sku" v-model="selectedItem.sku" disabled />
                                            <p-input label="Variant 1" v-model="selectedItem.variant1" disabled />
                                            <p-input label="Variant 2" v-model="selectedItem.variant2" disabled />
                                            <b-row class="mt-3">
                                                <b-col class="pb-1">
                                                    <p-checkbox label="Made To Order" :checked="isChecked" :key="selectedItem.specialItemGroupItemId" disabled />
                                                    <p-checkbox label="Send To KDS" v-model="selectedItem.sendToKds" />
                                                </b-col>
                                                <b-col>
                                                    <p-button v-b-modal.ConfigureMadeToOrder>Configure Made To Order</p-button>
                                                </b-col>
                                            </b-row>
                                        </div>
                                        <p-input v-else label="Container Label" v-model="selectedItem.containerName" @input="containerNameChanged" :disabled="selectedItem.specialColumnType > 0 || (selectedItem.repeatablesContainer && !pTree1)" />
                                        <b-row>
                                            <b-col class="pt-1">
                                                <p-button variant="outline-danger" @click="removeSelectedItem">Remove</p-button>
                                            </b-col>
                                        </b-row>
                                    </div>
                                </p-card>
                            </b-col>
                        </b-row>
                    </p-card>
                    <b-row>
                        <b-col>
                            <p-button 
                                variant="danger"
                                @click="deleteProfile"
                                :disabled="disableDelete"
                                >Delete</p-button>
                        </b-col>
                        <b-col cols="auto" class="ms-auto">
                            <p-button variant="primary" type="submit" :is-busy="isBusy">Save</p-button>
                        </b-col>
                    </b-row>
                </div>
            </p-form>
            <ConfigureMadeToOrder v-if="selectedItem" v-model="selectedItem" />
        </b-overlay>
    </p-feature-flag>
</template>

<script>
import NavigationGuard from '@/components/mixins/NavigationGuard.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ProductDraggableSearch from '@/components/ProductDraggableSearch.vue';
import { currencyFormatter } from '@/components/Common/Formatters.js';
import ConfigureMadeToOrder from './ConfigureMadeToOrder.vue';
import editPage from '@/components/mixins/EditPage';
import axios from 'axios';

export default {
    mixins: [NavigationGuard, editPage],
    components: { ConfigureMadeToOrder, ProductDraggableSearch},
    data() {
        return {
            quickMenu: null,
            isBusy: false,
            selectedItem: null,
            pTree1: false,
            lastSelectedItem: null,
            disableDelete: false,
            specialContainerTypes: [{key: 1, value: 'DCC Sale'}, {key: 2, value: 'Gift Card'}, {key: 3, value: 'Pay-In'}, {key: 4, value: 'Pay-Out'}]
        };
    },
    methods: {  
        currencyFormatter: currencyFormatter,
        deleteProfile()
        {   
            let profileId = this.quickMenu.specialItemProfileId;
            this.isBusy = true;
            this.$bvModal
                .msgBoxConfirm(
                    'Are you sure you want to delete this Quick Menu Profile?',
                    {
                        okVariant: 'danger',
                        okTitle: 'Yes',
                        cancelTitle: 'No'
                    }
                )
                .then(value => {
                    if (value) {
                         axios
                            .post('quickmenus/delete', { specialItemProfileId : profileId })
                            .then(response => {
                                if (response) {
                                    this.dataSaved();
                                    this.$router.push('/quickmenus/'); 
                                }                        
                            })
                            .finally(() => {
                                    this.isBusy = false;
                            });
                    }else{
                        this.isBusy = false;
                    }
                });
        },
        isSpecialContainerSelected(type)
        {
            const groups = this.quickMenu.specialItemProfileGroups;
            if (!Array.isArray(groups)) return false;
            return groups.some(x => x.specialColumnType === type.key);
        },
        toggleSpecialContainer(type) {
            const index = this.quickMenu.specialItemProfileGroups.findIndex(x => x.specialColumnType === type.key);
            if (index === -1) {
                this.addSpecialColumnContainer(type);
            } else {
                this.quickMenu.specialItemProfileGroups.splice(index, 1);
            }
        },
        itemSelected(event, pTree1) {   
            if(this.lastSelectedItem !== event)
            {                
                this.pTree1 = pTree1; 
                
                if(this.lastSelectedItem && this.lastSelectedItem.isSelected)
                {
                    this.lastSelectedItem.isSelected = false;
                }

                this.lastSelectedItem = event;
                this.selectedItem = event;
            }
        },
        async loadData() {
            this.isBusy = true;
            this.disableDelete = this.isNew;
            let promise = this.isNew
                ? axios.get('QuickMenus/new')
                : axios.get('QuickMenus', { params: { id: this.id } });

            promise.then(resp => {
                this.quickMenu = resp.data;

                if (!this.isNew) {
                      this.dataLoaded();                      
                }
                this.isBusy = false;
            });
        },
        deepClone(item) {
            var newContainer = JSON.parse(JSON.stringify(item));
            newContainer.expanded = false;
            newContainer.aliasForGroupId = newContainer.specialItemGroupItemId;
            return newContainer;
        },
        async onSubmit(){
            this.quickMenu.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

            this.isBusy = true;
                axios
                    .post('quickmenus', this.quickMenu)
                    .then(response => {
                        this.quickMenu = response.data;                        

                        if (this.isNew) {
                            this.$router.push('/quickmenus/' + this.quickMenu.specialItemProfileId); 
                        }
                        
                        this.$appSuccess(`Quick Menu '${this.quickMenu.name}' saved successfully.`);
                        this.dataLoaded();
                        this.isBusy = false;
                    })
                    .finally(() => {
                            this.isBusy = false;
                    });
        },
        removeSelectedItem()
        {
            if (!this.selectedItem) return;

            // Try to remove from top-level groups
            var index = this.quickMenu.specialItemProfileGroups.indexOf(this.selectedItem);
            if (index !== -1) {
                this.quickMenu.specialItemProfileGroups.splice(index, 1);
                this.selectedItem = null;
                return;
            }

            // Search through all groups' children recursively
            for (var group of this.quickMenu.specialItemProfileGroups) {
                if (this.removeFromChildren(group.specialItemGroupItems, this.selectedItem)) {
                    this.selectedItem = null;
                    return;
                }
            }
        },
        removeFromChildren(items, target) {
            if (!items) return false;
            var index = items.indexOf(target);
            if (index !== -1) {
                items.splice(index, 1);
                return true;
            }
            for (var item of items) {
                if (item.specialItemGroupItems && this.removeFromChildren(item.specialItemGroupItems, target)) {
                    return true;
                }
            }
            return false;
        },
        addPage(){
            this.quickMenu.specialItemProfileGroups.push(this.getBlankItem("New Page", false));
        },
        addContainer(){
            if(this.selectedItem && this.selectedItem.isContainer)
            {
                this.selectedItem.specialItemGroupItems.push(this.getBlankItem("New Container", false));
            }
        },
        addRepeatableContainer(){
            this.quickMenu.repeatablesContainer.specialItemGroupItems.push(this.getBlankItem("New Repeatable Container", true));
        },
        addSpecialColumnContainer(type)
        {
            var blankItem = this.getBlankItem(type.value, false);
            blankItem.specialColumnType = type.key;

            this.quickMenu.specialItemProfileGroups.push(blankItem);
        },
        getBlankItem(name, repeatablesContainer)
        {
            return {
                    expanded: true,
                    specialItemProfileGroupId: 0, 
                    specialItemProfileId: this.quickMenu.SpecialItemProfileId,
                    specialItemGrouItemId: 0,
                    specialItemGroupName: name,
                    containerName: name,
                    displayOrder: 0,
                    isContainer: true,
                    repeatablesContainer: repeatablesContainer,
                    sendToKds: false,
                    specialItemGroupItems: []                
                };
        },
        showLock(node){
            if(node)
            {
                return (node.specialColumnType !== undefined && node.specialColumnType !== null) || node.repeatablesContainer;
            }
        },
        containerNameChanged()
        {
            //find any used repeatable containers and update them with the new name.
            if(this.selectedItem.repeatablesContainer)
            {
                this.quickMenu.specialItemProfileGroups.forEach(item => {
                    this.recursiveUpdateRepeatableContainerName(item);
                });
            }
        },
        recursiveUpdateRepeatableContainerName(item)
        {
            if(item.repeatablesContainer)
            {
                if(item.specialItemGroupName == this.selectedItem.specialItemGroupName)
                {
                    item.specialItemGroupName = this.selectedItem.containerName;
                }
            }

            if(item.specialItemGroupItems.length > 0)
            {
                item.specialItemGroupItems.forEach(item => {
                    this.recursiveUpdateRepeatableContainerName(item);
                });
            }
        }
    },
    mounted: async function() {
        await this.loadData();
    },
    computed: {
        isChecked() {
            return this.selectedItem?.madeToOrderPrompts?.length > 0;
        }
    }
};
</script>

<style scoped lang="scss">
.content-wrapper {
    border: 0px;
    min-width: 0;
}

.product-search-container {
    max-height: 93%; 
    height: 93%; 
    padding-bottom: 0px; // this is here to keep the input from pushing up behind the container.    
}

:deep(.product-search-container .card-body ) {
    padding-bottom: 0px !important;
}

:deep(.no-bullets) {
    min-width: 0;
    overflow: hidden;
}

:deep(.no-bullets .d-flex) {
    min-width: 0;
}

:deep(.node-text) {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>